import { useTranslation } from "react-i18next";
import { DataTable } from "../components/table/DataTable";
import { useCallback, useState } from "react";
import Form from "../components/form/Form";
import { Modal } from "../components/modal/Modal";
import { useQuery } from "@tanstack/react-query";
import { useTrainingRoute } from "../../core/hooks/useTrainingRoute";
import { useAdminPermissions } from "../../core/hooks/useAdminPermissions";
import testimonyTableConfig from "./config/testimony/testimonyTableConfig";
import testimonyFormConfig from "./config/testimony/testimonyFormConfig";
import PageLayout from "../components/layout/PageLayout";
import {
  emptyTestimony,
  Testimony,
  TestimonyFormData,
} from "../../core/testimony/entities/Testimony";
import { useTestimonyFormValidation } from "../../core/hooks/form/useTestimonyFormValidation";
import { getAllTestimoniesUseCase } from "../../core/testimony/useCases/getAllTestimoniesUseCase";
import { deleteTestimonyUseCase } from "../../core/testimony/useCases/deleteTestimonyUseCase";
import { addUpdateTestimonyUseCase } from "../../core/testimony/useCases/addUpdateTestimonyUseCase";
import { updateTestimonyOrderUseCase } from "../../core/testimony/useCases/updateTestimonyOrderUseCase";
import { HistoryButton } from "../components/history/HistoryButton";
export default function CategoryList() {
  const { t } = useTranslation();
  const { trainingId } = useTrainingRoute();
  const permissions = useAdminPermissions();

  const [selected, setSelected] = useState<Testimony | null>(null);
  const form = useTestimonyFormValidation(selected, t);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["testimony", trainingId],
    queryFn: async () => {
      return await getAllTestimoniesUseCase.exec(trainingId!);
    },
    enabled: !!trainingId,
  });

  const handleReorder = useCallback(
    async (list: Testimony[]) => {
      const success = await updateTestimonyOrderUseCase.exec(list);
      if (success) refetch();
    },
    [refetch]
  );

  return (
    <PageLayout isLoading={isLoading}>
      <DataTable
        title={t("testimony.title")}
        datas={data || []}
        columns={testimonyTableConfig(t)}
        filters={["title", "description"]}
        onRowClicked={(row: Testimony) => {
          setSelected(row);
        }}
        onAdd={
          permissions.canCreate
            ? () => setSelected(emptyTestimony(trainingId!) as Testimony)
            : undefined
        }
        onReorder={permissions.canReorder ? handleReorder : undefined}
      />

      <Modal
        isOpen={!!selected}
        onClose={() => {
          setSelected(null);
        }}
        title={selected?.id ? t("form.edit") : t("form.add")}
        actions={
          selected?.id ? (
            <HistoryButton
              collection="testimonies"
              recordId={selected.id}
              recordLabel={selected.title}
              onRestored={() => {
                setSelected(null);
                refetch();
              }}
            />
          ) : undefined
        }
      >
        <Form
          className="md:w-[600px]"
          id={selected?.id}
          onSave={async (
            data: TestimonyFormData,
            id: string | number | null
          ) => {
            const success = await addUpdateTestimonyUseCase.exec(
              data as TestimonyFormData,
              id
            );
            if (success) {
              setSelected(null);
              refetch();
            }
          }}
          onDelete={async (id: string | number) => {
            const success = await deleteTestimonyUseCase.exec(id);
            if (success) {
              setSelected(null);
              refetch();
            }
          }}
          form={form}
          formConfig={testimonyFormConfig}
          onCancel={() => setSelected(null)}
        />
      </Modal>
    </PageLayout>
  );
}
